<template>
    <div class="custom-stepper">
        <!-- Stepper Header -->
        <div class="stepper-header">
            <div 
                v-for="(step, index) in steps" 
                :key="index"
                class="stepper-step"
                :class="{
                    'active': currentStep === index + 1,
                    'completed': currentStep > index + 1,
                    'disabled': currentStep < index + 1
                }"
                :aria-current="currentStep === index + 1 ? 'step' : undefined"
            >
                <div class="step-circle">
                    <span v-if="currentStep > index + 1" class="step-check">
                        <span class="sr-only">{{ index + 1 }}</span>
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                        </svg>
                    </span>
                    <span v-else class="step-number">{{ index + 1 }}</span>
                </div>
                <div class="step-label">
                    {{ step }}
                    <span class="sr-only">{{ stepState(index) }}</span>
                </div>
                <div v-if="index < steps.length - 1" class="step-divider"></div>
            </div>
        </div>

        <!-- Stepper Content -->
        <div class="stepper-content">
            <slot :current-step="currentStep" />
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    steps: string[];
    currentStep: number;
}

const props = defineProps<Props>();

// Completed/current/upcoming is otherwise carried only by circle colour.
const stepState = (index: number): string => {
    if (props.currentStep > index + 1) return '(completed)';
    if (props.currentStep === index + 1) return '(current step)';
    return '(not started)';
};
</script>

<style lang="scss" scoped>
.custom-stepper {
    @apply w-full;
}

.stepper-header {
    @apply flex items-center justify-between px-4;
}

.stepper-step {
    @apply flex flex-col items-center relative flex-1;
    
    .step-circle {
        @apply w-10 h-10 rounded-full border-2 flex items-center justify-center mb-2 transition-all duration-200;
        background: var(--surface-sunken);
        border-color: var(--hairline);
        
        .step-number {
            @apply text-sm font-medium text-gray-500;
        }
        
        .step-check {
            @apply text-ink-invert;
        }
    }
    
    .step-label {
        @apply text-xs font-medium text-gray-500 text-center;
    }
    
    .step-divider {
        @apply absolute top-5 left-full w-full h-0.5 bg-gray-200;
        left: calc(50% + 20px);
        width: calc(100% - 40px);
        z-index: 1;
    }
    
    &.active {
        .step-circle {
            background: var(--action);
            border-color: var(--action);
            
            .step-number {
                @apply text-ink-invert;
            }
        }
        
        .step-label {
            @apply text-gray-900;
        }
    }
    
    &.completed {
        .step-circle {
            background: var(--success-mark);
            border-color: var(--success-mark);
        }
        
        .step-label {
            @apply text-gray-900;
        }
        
        .step-divider {
            @apply bg-green-200;
        }
    }
    
    &.disabled {
        .step-circle {
            background: var(--surface-sunken);
            border-color: var(--hairline);
            
            .step-number {
                @apply text-gray-400;
            }
        }
        
        .step-label {
            @apply text-gray-400;
        }
    }
}


</style> 